import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Target, Play, Pause, Archive } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Campaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    status: "active",
    description: "",
  });

  useEffect(() => {
    fetchCampaigns();
  }, [filterStatus]);

  const fetchCampaigns = async () => {
    try {
      const params = filterStatus !== "all" ? { status: filterStatus } : {};
      const response = await axios.get(`${API}/campaigns`, { params });
      setCampaigns(response.data);
    } catch (error) {
      console.error("Failed to fetch campaigns:", error);
      toast.error("Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const openCreateDialog = () => {
    setEditingCampaign(null);
    setFormData({ name: "", status: "active", description: "" });
    setDialogOpen(true);
  };

  const openEditDialog = (campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      name: campaign.name,
      status: campaign.status,
      description: campaign.description || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingCampaign) {
        await axios.put(`${API}/campaigns/${editingCampaign.id}`, formData);
        toast.success("Campaign updated successfully");
      } else {
        await axios.post(`${API}/campaigns`, formData);
        toast.success("Campaign created successfully");
      }
      setDialogOpen(false);
      fetchCampaigns();
    } catch (error) {
      const message = error.response?.data?.detail || "Operation failed";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (campaignId) => {
    if (!window.confirm("Are you sure you want to delete this campaign?")) return;

    try {
      await axios.delete(`${API}/campaigns/${campaignId}`);
      toast.success("Campaign deleted successfully");
      fetchCampaigns();
    } catch (error) {
      toast.error("Failed to delete campaign");
    }
  };

  const handleStatusChange = async (campaign, newStatus) => {
    try {
      await axios.put(`${API}/campaigns/${campaign.id}`, {
        ...campaign,
        status: newStatus,
      });
      toast.success(`Campaign ${newStatus}`);
      fetchCampaigns();
    } catch (error) {
      toast.error("Failed to update campaign status");
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { class: "badge-success", icon: Play, label: "Active" },
      paused: { class: "badge-warning", icon: Pause, label: "Paused" },
      closed: { class: "bg-secondary text-muted-foreground", icon: Archive, label: "Closed" },
    };
    const badge = badges[status] || badges.active;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-sm ${badge.class}`}>
        <badge.icon size={12} />
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-['Chivo']">Campaigns</h1>
          <p className="text-muted-foreground">Manage calling campaigns</p>
        </div>
        <Button
          onClick={openCreateDialog}
          data-testid="create-campaign-btn"
          className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wide rounded-sm"
        >
          <Plus size={18} className="mr-2" />
          New Campaign
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-48 bg-input/50" data-testid="status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Campaigns Table */}
      <Card className="bg-card border-border rounded-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="bg-secondary/50 text-muted-foreground uppercase text-xs font-bold">
                    Campaign Name
                  </TableHead>
                  <TableHead className="bg-secondary/50 text-muted-foreground uppercase text-xs font-bold">
                    Description
                  </TableHead>
                  <TableHead className="bg-secondary/50 text-muted-foreground uppercase text-xs font-bold">
                    Status
                  </TableHead>
                  <TableHead className="bg-secondary/50 text-muted-foreground uppercase text-xs font-bold">
                    Created
                  </TableHead>
                  <TableHead className="bg-secondary/50 text-muted-foreground uppercase text-xs font-bold text-right">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaigns.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      <Target size={48} className="mx-auto mb-4 opacity-50" />
                      <p>No campaigns found</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  campaigns.map((campaign) => (
                    <TableRow key={campaign.id} className="hover:bg-secondary/20">
                      <TableCell className="font-medium">{campaign.name}</TableCell>
                      <TableCell className="max-w-xs truncate">
                        {campaign.description || "-"}
                      </TableCell>
                      <TableCell>{getStatusBadge(campaign.status)}</TableCell>
                      <TableCell className="font-mono text-sm">
                        {new Date(campaign.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {campaign.status === "active" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleStatusChange(campaign, "paused")}
                              title="Pause Campaign"
                            >
                              <Pause size={16} />
                            </Button>
                          )}
                          {campaign.status === "paused" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleStatusChange(campaign, "active")}
                              title="Activate Campaign"
                            >
                              <Play size={16} />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(campaign)}
                          >
                            <Pencil size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(campaign.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-['Chivo']">
              {editingCampaign ? "Edit Campaign" : "Create New Campaign"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Campaign Name *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                data-testid="campaign-name-input"
                className="bg-input/50"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger data-testid="campaign-status-select" className="bg-input/50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                data-testid="campaign-description-input"
                className="bg-input/50"
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="save-campaign-btn"
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {submitting ? "Saving..." : editingCampaign ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Campaigns;
